import React, { useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  InputAdornment,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

interface SearchResult {
  id: string;
  title: string;
  regulation: string;
  section: string;
  content: string;
  matches: string[];
}

const Search: React.FC = () => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsLoading(true);

    // TODO: Implement actual search functionality
    // This is mock data
    setTimeout(() => {
      const mockResults: SearchResult[] = [
        {
          id: '1',
          title: 'Army Command Policy',
          regulation: 'AR 600-20',
          section: '2-1',
          content: 'Chain of Command responsibilities and procedures...',
          matches: ['command', 'policy'],
        },
        {
          id: '2',
          title: 'Military Justice',
          regulation: 'AR 27-10',
          section: '3-2',
          content: 'Procedures for conducting investigations and hearings...',
          matches: ['justice', 'investigation'],
        },
      ];
      setResults(mockResults);
      setIsLoading(false);
    }, 1000);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Box>
      <Paper
        elevation={3}
        sx={{
          p: 2,
          mb: 3,
          bgcolor: theme.palette.army.main,
        }}
      >
        <TextField
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Search Army regulations..."
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: theme.palette.army.accent }} />
                ) : (
                  <IconButton
                    onClick={handleSearch}
                    disabled={!searchQuery.trim()}
                    sx={{
                      color: theme.palette.army.accent,
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: theme.palette.background.default,
            },
          }}
        />
      </Paper>

      {results.length > 0 && (
        <List>
          {results.map((result) => (
            <Paper
              key={result.id}
              elevation={2}
              sx={{
                mb: 2,
                bgcolor: theme.palette.army.main,
                '&:hover': {
                  bgcolor: theme.palette.army.shield,
                },
              }}
            >
              <ListItem
                button
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ width: '100%', mb: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: theme.palette.army.textPrimary }}
                  >
                    {result.title}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: theme.palette.army.accent }}
                  >
                    {result.regulation} Section {result.section}
                  </Typography>
                </Box>
                <ListItemText
                  primary={result.content}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: theme.palette.army.textSecondary,
                    },
                  }}
                />
                <Box sx={{ mt: 1 }}>
                  {result.matches.map((match, index) => (
                    <Chip
                      key={index}
                      label={match}
                      size="small"
                      sx={{
                        mr: 1,
                        bgcolor: theme.palette.army.accent,
                        color: theme.palette.army.textPrimary,
                      }}
                    />
                  ))}
                </Box>
              </ListItem>
            </Paper>
          ))}
        </List>
      )}

      {searchQuery && !isLoading && results.length === 0 && (
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            color: theme.palette.army.textSecondary,
            mt: 4,
          }}
        >
          No results found for "{searchQuery}"
        </Typography>
      )}
    </Box>
  );
};

export default Search; 